import { REJECT, array, nullable, num, object, str, type Received } from 'validation';

import { getRecord } from 'api/shared/getRecord';

// Source of truth for the wire shape: nap-backend `routes/workflows.js` GET /workflows/:workflow_id. The node instance
// view shows an operator message's subject, each action's message, and lets the operator pick one of its response
// options. It renders no input fields and no dropdown parameters, so it declares none: a drift in those must not take
// away the buttons it does render.
//
// The text columns are nullable in `workflows` / `workflow_actions` and are copied from node configuration.

/** An answer the operator can give: a node definition handle, so `id` is a uuid and the thing `respond` sends back. */
export interface OperatorMessageResponseOption {
  readonly id: string;
  readonly label: string;
}

/** The answer given: the chosen option's label, or the bare label a cancelled node instance writes. */
export interface OperatorMessageResponse {
  readonly label: string;
  readonly user: string | null;
}

export interface OperatorMessageAction {
  readonly workflow_action_id: number;
  readonly subject: string | null;
  readonly message: string | null;
  readonly response: OperatorMessageResponse | null;
  readonly response_options: readonly OperatorMessageResponseOption[];
}

export interface OperatorMessage {
  readonly workflow_subject: string | null;
  readonly workflows_actions: readonly OperatorMessageAction[];
}

const messageSchema = object<OperatorMessage>({
  workflow_subject: nullable(str()),
  workflows_actions: array(
    object<OperatorMessageAction>({
      workflow_action_id: num(REJECT),
      subject: nullable(str()),
      message: nullable(str()),
      response: nullable(object<OperatorMessageResponse>({ label: str(), user: nullable(str()) })),
      response_options: array(object<OperatorMessageResponseOption>({ id: str(REJECT), label: str() })),
    })
  ),
});

/**
 * GET `/workflows/:workflow_id` for the operator message panel in the node instance view. See `getRecord` for the
 * outcomes: `null` is the server saying there is no such workflow.
 */
export const getOperatorMessage = (id: number): Promise<Received<OperatorMessage | null>> =>
  getRecord(`/workflows/${id}`, { schema: messageSchema, source: 'getOperatorMessage' });
